import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSchedule } from '@/hooks/useSchedule';
import { useUserScenario } from '@/hooks/useUserScenario';
import { memberPortalService } from '@/services/memberPortal';
import {
  mapMemberCourtHireBookingsToExtraEvents,
  type ConsumerCalendarExtraEvent,
} from '@/utils/consumerCalendarEvents';

export interface UseOrgConsumerScheduleOptions {
  hasLessonsAccess: boolean;
  hasCourtHire: boolean;
  hasInventoryHire?: boolean;
  academyId?: string | null;
  clubSlug?: string | null;
}

export function useOrgConsumerSchedule(options: UseOrgConsumerScheduleOptions) {
  const { children, selfStudent, academyId: scenarioAcademyId } = useUserScenario();
  const resolvedAcademyId = options.hasLessonsAccess
    ? options.academyId ?? scenarioAcademyId ?? undefined
    : undefined;

  const schedule = useSchedule({
    academyId: resolvedAcademyId,
  });

  const courtQuery = useQuery({
    queryKey: ['org-consumer-court-bookings', options.clubSlug],
    enabled: options.hasCourtHire && Boolean(options.clubSlug),
    queryFn: async () => {
      const res = await memberPortalService.getMyCourtHireBookings(options.clubSlug!);
      return res.success && res.data ? res.data : [];
    },
  });

  const inventoryQuery = useQuery({
    queryKey: ['org-consumer-inventory-hires', options.clubSlug],
    enabled: Boolean(options.clubSlug) && Boolean(options.hasCourtHire || options.hasInventoryHire),
    queryFn: async () => {
      const res = await memberPortalService.getMyInventoryHires(options.clubSlug!);
      return res.success && res.data ? res.data : [];
    },
  });

  const courtExtraEvents = useMemo(
    () =>
      mapMemberCourtHireBookingsToExtraEvents(
        courtQuery.data ?? [],
        inventoryQuery.data ?? []
      ),
    [courtQuery.data, inventoryQuery.data]
  );

  const studentsById = useMemo(() => {
    const map: Record<string, string> = {};
    [...children, ...(selfStudent ? [selfStudent] : [])].forEach((student) => {
      map[student.id] = `${student.firstName} ${student.lastName}`;
    });
    return map;
  }, [children, selfStudent]);

  const lessonLoading = options.hasLessonsAccess ? schedule.loading : false;
  const courtLoading = options.hasCourtHire ? courtQuery.isLoading : false;
  const inventoryLoading =
    options.hasCourtHire || options.hasInventoryHire ? inventoryQuery.isLoading : false;

  return {
    lessonOccurrences: options.hasLessonsAccess ? schedule.occurrences : [],
    makeupOccurrenceSet: options.hasLessonsAccess ? schedule.makeupOccurrenceSet : new Set<string>(),
    scheduleData: options.hasLessonsAccess ? schedule.data : null,
    courtBookings: courtQuery.data ?? [],
    inventoryHires: inventoryQuery.data ?? [],
    courtExtraEvents: (options.hasCourtHire ? courtExtraEvents : []) as ConsumerCalendarExtraEvent[],
    studentsById,
    lessonLoading,
    courtLoading,
    inventoryLoading,
    isLoading: lessonLoading || courtLoading || inventoryLoading,
    refetchSchedule: schedule.refetch,
  };
}
